import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import axios from 'axios';
import './ImageSlider.css';

// Custom Video Player component that handles authentication for streaming
const VideoPlayer = ({ src, token, videoRef, onError }) => {
  const [videoSrc, setVideoSrc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const createAuthenticatedVideoUrl = async () => {
      try {
        setLoading(true);
        
        // Create a custom URL with authentication token embedded
        const authenticatedUrl = `${src}?auth=${encodeURIComponent(token)}`;
        
        // For now, let's test if direct URL with token works
        // Later we can implement proper streaming if needed
        setVideoSrc(authenticatedUrl);
        setLoading(false);
      } catch (error) {
        console.error('Error setting up video:', error);
        setLoading(false);
        onError();
      }
    };

    createAuthenticatedVideoUrl();
  }, [src, token, onError]);

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center bg-dark" style={{ height: '400px' }}>
        <Spinner animation="border" variant="light" size="lg" />
        <span className="text-white ms-3">Loading video...</span>
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      src={videoSrc}
      controls
      preload="metadata"
      style={{ 
        width: '100%', 
        maxHeight: '70vh',
        backgroundColor: '#000'
      }}
      controlsList="nodownload"
      onError={(e) => {
        console.log('Video playback error');
        onError();
      }}
      onLoadStart={() => console.log('Video loading started')}
      onCanPlay={() => console.log('Video can start playing')}
      onProgress={() => console.log('Video buffering...')}
    />
  );
};

// PDF Viewer component that handles authentication for PDF viewing
const PdfViewer = ({ src, token }) => {
  const [pdfSrc, setPdfSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const createAuthenticatedPdfUrl = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Create authenticated URL for PDF
        const authenticatedUrl = `${src}?auth=${encodeURIComponent(token)}`;
        setPdfSrc(authenticatedUrl);
        setLoading(false);
      } catch (err) {
        console.error('Error setting up PDF:', err);
        setError('Failed to load PDF');
        setLoading(false);
      }
    };

    createAuthenticatedPdfUrl();
  }, [src, token]);

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: '600px' }}>
        <Spinner animation="border" variant="primary" size="lg" />
        <span className="ms-3">Loading PDF...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="d-flex align-items-center justify-content-center text-danger" style={{ height: '400px' }}>
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="pdf-viewer-container">
      <iframe
        src={pdfSrc}
        style={{
          width: '100%',
          height: 'calc(100vh - 120px)',
          border: 'none',
          borderRadius: '0'
        }}
        title="PDF Viewer"
        onLoad={() => console.log('PDF loaded successfully')}
        onError={() => setError('Failed to load PDF')}
      />
      <div className="position-absolute" style={{ bottom: '10px', right: '10px' }}>
        <a 
          href={pdfSrc} 
          target="_blank" 
          rel="noopener noreferrer"
          className="btn btn-light btn-sm shadow"
        >
          <i className="bi bi-box-arrow-up-right me-1"></i>
          Open in New Tab
        </a>
      </div>
    </div>
  );
};

export default function ObjectPreviewModal({ show, onHide, obj, token, objects = [] }) {
  const API_BASE = 'https://r2-object-manager.vijay-vishwakarma.workers.dev';
  
  // Memoize file type checks
  const { isImage, isVideo, isPdf } = useMemo(() => ({
    isImage: obj.key.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i),
    isVideo: obj.key.match(/\.(mp4|webm|ogg|avi|mov|wmv|flv|mkv)$/i),
    isPdf: obj.key.match(/\.pdf$/i)
  }), [obj.key]);
  
  const [playing, setPlaying] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const videoRef = useRef();

  // Get all images from objects for slider functionality
  const allImages = useMemo(() => 
    objects.filter(o => o.key.match(/\.(jpg|jpeg|png|gif)$/i)),
    [objects]
  );

  // Find current image index in the slider
  useEffect(() => {
    if (isImage && allImages.length > 0) {
      const index = allImages.findIndex(img => img.key === obj.key);
      setCurrentIndex(index >= 0 ? index : 0);
    }
  }, [obj.key, isImage, allImages]);

  // Current object for display (for image slider)
  const currentObj = isImage && allImages.length > 0 ? allImages[currentIndex] : obj;

  // Memoize the preview URL
  const previewUrl = useMemo(() => 
    `${API_BASE}/api/preview/${encodeURIComponent(currentObj.key)}`,
    [currentObj.key]
  );

  useEffect(() => {
    if (!show) {
      setBlobUrl(null);
      setPlaying(false);
      setLoading(false);
      return;
    }
    
    if (isImage) {
      // For images, use the existing data URL approach for compatibility
      setLoading(true);
      setBlobUrl(null);
      
      let isMounted = true;
      let cancelToken = axios.CancelToken.source();
      
      axios.get(previewUrl, {
        headers: { Authorization: 'Basic ' + token },
        responseType: 'blob',
        cancelToken: cancelToken.token
      }).then(res => {
        if (!isMounted) return;
        
        const reader = new FileReader();
        reader.onload = () => {
          if (isMounted) {
            setBlobUrl(reader.result);
            setLoading(false);
          }
        };
        reader.onerror = () => {
          if (isMounted) {
            console.error('FileReader error for', currentObj.key);
            setBlobUrl(null);
            setLoading(false);
          }
        };
        reader.readAsDataURL(res.data);
      }).catch(error => {
        if (isMounted && !axios.isCancel(error)) {
          console.error('Preview fetch error for', currentObj.key, error);
          setBlobUrl(null);
          setLoading(false);
        }
      });
      
      return () => {
        isMounted = false;
        cancelToken.cancel('Component unmounted');
      };
    } else if (isVideo && playing) {
      // For videos, we'll set the streaming URL directly
      // The worker should handle range requests for streaming
      setBlobUrl(previewUrl);
      setLoading(false);
    }
  }, [show, playing, previewUrl, token, isImage, isVideo, isPdf, currentObj.key]);

  const navigateImage = (direction) => {
    if (allImages.length <= 1) return;
    
    let newIndex;
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : allImages.length - 1;
    } else {
      newIndex = currentIndex < allImages.length - 1 ? currentIndex + 1 : 0;
    }
    setCurrentIndex(newIndex);
  };

  const handleKeyDown = (e) => {
    if (!show || !isImage) return;
    
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      navigateImage('prev');
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      navigateImage('next');
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onHide();
    }
  };

  useEffect(() => {
    if (show && isImage) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [show, isImage, currentIndex, allImages.length]);

  if (isImage) {
    return (
      <Modal 
        show={show} 
        onHide={onHide} 
        size="xl" 
        centered 
        className="image-modal"
        style={{ zIndex: 9999 }}
      >
        <Modal.Header 
          closeButton 
          className="bg-dark text-white border-0"
          style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10, background: 'rgba(0,0,0,0.8)' }}
        >
          <Modal.Title className="d-flex align-items-center justify-content-between w-100">
            <span>{currentObj.key}</span>
            {allImages.length > 1 && (
              <small className="text-muted ms-3">
                {currentIndex + 1} of {allImages.length}
              </small>
            )}
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body 
          className="p-0 position-relative d-flex align-items-center justify-content-center"
          style={{ 
            minHeight: '80vh', 
            backgroundColor: '#000',
            overflow: 'hidden'
          }}
        >
          {loading ? (
            <div className="d-flex align-items-center justify-content-center" style={{ height: '80vh' }}>
              <Spinner animation="border" variant="light" size="lg" />
            </div>
          ) : blobUrl ? (
            <div className="position-relative w-100 h-100 d-flex align-items-center justify-content-center">
              <img 
                src={blobUrl} 
                alt={currentObj.key} 
                style={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                  userSelect: 'none'
                }}
                draggable={false}
              />
              
              {/* Navigation Buttons */}
              {allImages.length > 1 && (
                <>
                  <Button
                    variant="dark"
                    className="position-absolute start-0 top-50 translate-middle-y ms-3"
                    style={{ 
                      opacity: 0.8,
                      borderRadius: '50%',
                      width: '50px',
                      height: '50px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={() => navigateImage('prev')}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z" />
                    </svg>
                  </Button>
                  
                  <Button
                    variant="dark"
                    className="position-absolute end-0 top-50 translate-middle-y me-3"
                    style={{ 
                      opacity: 0.8,
                      borderRadius: '50%',
                      width: '50px',
                      height: '50px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={() => navigateImage('next')}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z" />
                    </svg>
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="text-white">Failed to load image</div>
          )}
        </Modal.Body>
        
        {allImages.length > 1 && (
          <Modal.Footer 
            className="bg-dark border-0 justify-content-center"
            style={{ background: 'rgba(0,0,0,0.8)' }}
          >
            <div className="d-flex gap-2">
              {allImages.map((img, idx) => (
                <div
                  key={img.key}
                  className={`bg-secondary rounded ${idx === currentIndex ? 'bg-primary' : ''}`}
                  style={{
                    width: '8px',
                    height: '8px',
                    cursor: 'pointer',
                    opacity: idx === currentIndex ? 1 : 0.5
                  }}
                  onClick={() => setCurrentIndex(idx)}
                />
              ))}
            </div>
          </Modal.Footer>
        )}
      </Modal>
    );
  }

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size={isPdf ? "xl" : "lg"} 
      centered 
      className={isPdf ? "image-modal" : ""}
    >
      <Modal.Header closeButton className="bg-dark text-white">
        <Modal.Title>{obj.key}</Modal.Title>
      </Modal.Header>
      <Modal.Body className={`${isPdf ? 'p-0' : 'bg-dark p-0'}`}>
        {isVideo && (
          <div className="position-relative">
            {!playing ? (
              <div 
                className="position-relative bg-dark d-flex align-items-center justify-content-center video-overlay"
                style={{ 
                  height: '400px', 
                  cursor: 'pointer',
                  backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundSize: 'cover'
                }}
                onClick={() => setPlaying(true)}
              >
                <div className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center">
                  <Button
                    className="video-play-button rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: '80px',
                      height: '80px',
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      border: '3px solid rgba(255,255,255,0.3)',
                      color: '#000',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
                    }}
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8,5.14V19.14L19,12.14L8,5.14Z" />
                    </svg>
                  </Button>
                </div>
                <div className="text-white text-center position-absolute" style={{ bottom: '20%' }}>
                  <h5 className="mb-2 fw-bold">{obj.key}</h5>
                  <p className="text-white-50 mb-0">Click to play video</p>
                </div>
              </div>
            ) : loading ? (
              <div className="d-flex align-items-center justify-content-center bg-dark" style={{ height: '400px' }}>
                <Spinner animation="border" variant="light" size="lg" />
                <span className="text-white ms-3">Loading video...</span>
              </div>
            ) : (
              blobUrl && (
                <VideoPlayer
                  src={blobUrl}
                  token={token}
                  videoRef={videoRef}
                  onError={() => {
                    console.error('Video playback error');
                    setPlaying(false);
                    setBlobUrl(null);
                  }}
                />
              )
            )}
          </div>
        )}
        {isPdf && (
          <PdfViewer src={previewUrl} token={token} />
        )}
        {!isImage && !isVideo && !isPdf && (
          <div className="text-center p-4">
            <div className="text-muted">No preview available for this file type.</div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
